import {
  CONTENT_WIDTH,
  bottomMarginItemGlobal
} from "../../utils/pdfConstants";


export function renderWeatherBlock({
  data = {},
  content = [],
  fontSize = 10,
  contentWidth = CONTENT_WIDTH
} = {}) {

  // -------------------------------------------------------
  // DATA
  // -------------------------------------------------------

  const weatherDataRaw = Array.isArray(data.dayWeather)
    ? data.dayWeather
    : [];

  // FMI may contain the next day's 00:00 point.
  // Keep only the first 24 hourly points.
  const weatherData = weatherDataRaw.slice(0, 24);

  const date = data.date || "";
  const startTime = data.startTime || "";
  const endTime = data.endTime || "";

  const startHour = getHourFromTime(startTime);

  const endHour =
    getHourFromTime(endTime) ??
    startHour;


  // -------------------------------------------------------
  // SELECTED INSPECTION TIME WEATHER
  // -------------------------------------------------------

  const minSelectedHour =
    startHour !== null
      ? Math.min(
          startHour,
          endHour ?? startHour
        )
      : null;

  const maxSelectedHour =
    startHour !== null
      ? Math.max(
          startHour,
          endHour ?? startHour
        )
      : null;

  const selectedWeather =
    weatherData.filter((item) => {
      const hour =
        getWeatherHour(item);

      if (
        hour === null ||
        minSelectedHour === null ||
        maxSelectedHour === null
      ) {
        return false;
      }

      return (
        hour >= minSelectedHour &&
        hour <= maxSelectedHour
      );
    });


  const average = (field) => {
    const values =
      selectedWeather
        .map((item) =>
          toValidNumber(
            item[field]
          )
        )
        .filter(
          (value) =>
            value !== null
        );

    if (!values.length) {
      return null;
    }

    return (
      values.reduce(
        (sum, value) =>
          sum + value,
        0
      ) /
      values.length
    );
  };


  const selectedTemperature =
    average("temperature");

  const selectedHumidity =
    average("humidity");

  const selectedWindSpeed =
    average("windSpeed");


  // Wind direction needs circular averaging.
  //
  // Example:
  // 350° + 10°
  //
  // Normal average = 180° -> wrong
  // Circular average = 0° -> correct
  const selectedWindDirectionDegrees =
    circularAverageDegrees(
      selectedWeather
        .map((item) =>
          toValidNumber(
            item.windDirection
          )
        )
        .filter(
          (value) =>
            value !== null
        )
    );


  const selectedWindDirection =
    selectedWindDirectionDegrees !== null
      ? windDirectionToFinnish(
          selectedWindDirectionDegrees
        )
      : "";


  // -------------------------------------------------------
  // HEADER
  // -------------------------------------------------------

 


  // -------------------------------------------------------
  // BASIC WEATHER INFORMATION
  // -------------------------------------------------------

  content.push({

    table: {

      widths: [
        "*",
        "*",
        "*",
        "*"
      ],

      body: [

        // HEADERS

        [
          {
            text:
              "Päivämäärä",

            bold: true,

            font:
              "Lato",

            fontSize
          },

          {
            text:
              "Tarkastusaika",

            bold: true,

            font:
              "Lato",

            fontSize
          },

          {
            text:
              "Lämpötila",

            bold: true,

            font:
              "Lato",

            fontSize
          },

          {
            text:
              "Kosteus",

            bold: true,

            font:
              "Lato",

            fontSize
          }
        ],


        // VALUES

        [
          {
            text:
              formatDate(date),

            font:
              "Lato",

            fontSize
          },


          {
            text:
              startTime
                ? `${startTime}${
                    endTime &&
                    endTime !== startTime
                      ? ` – ${endTime}`
                      : ""
                  }`
                : "-",

            font:
              "Lato",

            fontSize
          },


          {
            text:
              selectedTemperature !== null
                ? `${selectedTemperature.toFixed(
                    1
                  )} °C`
                : "-",

            font:
              "Lato",

            fontSize
          },


          {
            text:
              selectedHumidity !== null
                ? `${selectedHumidity.toFixed(
                    0
                  )} %`
                : "-",

            font:
              "Lato",

            fontSize
          }
        ]
      ]
    },


    layout: {

      hLineWidth:
        () => 0,

      vLineWidth:
        () => 0,

      paddingLeft:
        () => 4,

      paddingRight:
        () => 4,

      paddingTop:
        () => 3,

      paddingBottom:
        () => 3
    },


    margin: [
      0,
      0,
      0,
      8
    ]
  });


  // -------------------------------------------------------
  // GRAPH
  // -------------------------------------------------------

    // -------------------------------------------------------
  // GRAPH
  // -------------------------------------------------------

  if (
    weatherData.length > 0
  ) {

    const temperatures =
      weatherData
        .map(
          (item) =>
            toValidNumber(
              item.temperature
            )
        )
        .filter(
          (value) =>
            value !== null
        );


    if (
      temperatures.length > 0
    ) {

      const graphHeight =
        130;

      const left =
        35;

      const right =
        10;

      // Only small internal graph padding now.
      // Clouds + hours are OUTSIDE and ABOVE the graph.
      const top =
        10;


      const graphWidth =
        contentWidth -
        left -
        right;


      let minTemp =
        Math.floor(
          Math.min(
            ...temperatures
          )
        ) - 2;


      let maxTemp =
        Math.ceil(
          Math.max(
            ...temperatures
          )
        ) + 2;


      if (
        minTemp === maxTemp
      ) {
        minTemp -= 1;
        maxTemp += 1;
      }


      // ---------------------------------------------------
      // X POSITION
      // ---------------------------------------------------

      const xForIndex =
        (index) => {

          if (
            weatherData.length === 1
          ) {
            return (
              left +
              graphWidth / 2
            );
          }


          return (
            left +
            (
              index /
              (
                weatherData.length -
                1
              )
            ) *
            graphWidth
          );
        };


      // ---------------------------------------------------
      // Y POSITION
      // ---------------------------------------------------

      const yForTemperature =
        (temperature) =>

          top +

          (
            (
              maxTemp -
              temperature
            ) /
            (
              maxTemp -
              minTemp
            )
          ) *

          graphHeight;


      // ---------------------------------------------------
      // HOURLY WEATHER INFORMATION
      // ---------------------------------------------------

      const hourlyCellWidth =
        graphWidth /
        weatherData.length;


      // ---------------------------------------------------
      // WEATHER ICON ROW
      // ---------------------------------------------------

      const weatherIconRow =
        weatherData.map(
          (item) => {

            const iconCanvas = [];

            drawWeatherIcon(
              iconCanvas,
              getWeatherType(item),
              hourlyCellWidth / 2,
              8
            );

            return {
              canvas:
                iconCanvas,

              margin: [
                0,
                0,
                0,
                2
              ]
            };
          }
        );


      // ---------------------------------------------------
      // HOUR ROW
      // ---------------------------------------------------

      const hourRow =
        weatherData.map(
          (item) => ({

            text:
              item.hour
                ?.split(":")[0] ??
              "",

            alignment:
              "center",

            font:
              "Lato",

            fontSize:
              6,

            color:
              "#555555",

            margin: [
              0,
              0,
              0,
              3
            ]
          })
        );


      // ---------------------------------------------------
      // WIND DIRECTION ROW
      // ---------------------------------------------------

      const windDirectionRow =
        weatherData.map(
          (item) => {

            const direction =
              toValidNumber(
                item.windDirection
              );

            const arrowCanvas =
              [];

            if (
              direction !== null
            ) {

              drawWindArrow(
                arrowCanvas,
                hourlyCellWidth / 2,
                5,
                direction
              );
            }

            return {
              canvas:
                arrowCanvas,

              margin: [
                0,
                2,
                0,
                3
              ]
            };
          }
        );


      // ---------------------------------------------------
      // WIND SPEED ROW
      // ---------------------------------------------------

      const windSpeedRow =
        weatherData.map(
          (item) => {

            const speed =
              toValidNumber(
                item.windSpeed
              );

            return {

              text:
                speed !== null
                  ? speed.toFixed(1)
                  : "-",

              alignment:
                "center",

              font:
                "Lato",

              fontSize:
                5.5,

              color:
                "#666666",

              margin: [
                0,
                0,
                0,
                0
              ]
            };
          }
        );


      // ===================================================
      // CLOUDS + HOURS
      // ABOVE GRAPH
      // ===================================================

      content.push({

        columns: [

          // Same left edge as graph
          {
            width:
              left,

            text:
              ""
          },


          {
            width:
              graphWidth,

            table: {

              widths:
                weatherData.map(
                  () => "*"
                ),

              body: [

                // EXACT SAME GOOD ROWS AS BEFORE
                weatherIconRow,
                hourRow
              ]
            },


            layout: {

              hLineWidth:
                () => 0,

              vLineWidth:
                () => 0,

              paddingLeft:
                () => 0,

              paddingRight:
                () => 0,

              paddingTop:
                () => 0,

              paddingBottom:
                () => 0
            }
          },


          {
            width:
              right,

            text:
              ""
          }
        ],


        columnGap:
          0,


        margin: [
          0,
          3,
          0,
          4
        ]
      });


      // ===================================================
      // GRAPH CANVAS
      // ===================================================

      const canvas = [];


      // ---------------------------------------------------
      // SELECTED TIME BACKGROUND
      // ---------------------------------------------------

      if (
        startHour !== null &&
        endHour !== null
      ) {

        const selectedIndexes =
          weatherData
            .map(
              (item, index) => ({
                index,

                hour:
                  getWeatherHour(
                    item
                  )
              })
            )
            .filter(
              (item) =>

                item.hour !== null &&

                item.hour >=
                  Math.min(
                    startHour,
                    endHour
                  ) &&

                item.hour <=
                  Math.max(
                    startHour,
                    endHour
                  )
            );


        if (
          selectedIndexes.length > 0
        ) {

          const first =
            selectedIndexes[0]
              .index;


          const last =
            selectedIndexes[
              selectedIndexes.length -
              1
            ].index;


          const x1 =
            xForIndex(first);


          const x2 =
            xForIndex(last);


          canvas.push({

            type:
              "rect",

            x:
              x1,

            y:
              top,

            w:
              Math.max(
                x2 - x1,
                2
              ),

            h:
              graphHeight,

            color:
              "#eeeeee",

            lineColor:
              "#bbbbbb",

            lineWidth:
              0.5
          });
        }
      }


      // ---------------------------------------------------
    // ---------------------------------------------------
// GRID + TEMPERATURE VALUES
// ---------------------------------------------------

const gridLines = 5;

const temperatureLabels = [];

for (
  let i = 0;
  i <= gridLines;
  i++
) {

  const ratio =
    i / gridLines;


  const y =
    top +
    ratio *
    graphHeight;


  // Temperature represented by this grid line
  const temperatureValue =
    maxTemp -
    ratio *
    (
      maxTemp -
      minTemp
    );


  // Horizontal grid line
  canvas.push({

    type:
      "line",

    x1:
      left,

    y1:
      y,

    x2:
      left +
      graphWidth,

    y2:
      y,

    lineWidth:
      0.5,

    lineColor:
      "#dddddd"
  });


  temperatureLabels.push({
    temperature:
      Math.round(
        temperatureValue
      ),

    y
  });
}


      // ---------------------------------------------------
      // TEMPERATURE LINE
      // ---------------------------------------------------

      let previousPoint =
        null;


      weatherData.forEach(
        (item, index) => {

          const temperature =
            toValidNumber(
              item.temperature
            );


          if (
            temperature === null
          ) {

            previousPoint =
              null;

            return;
          }


          const point = {

            x:
              xForIndex(
                index
              ),

            y:
              yForTemperature(
                temperature
              )
          };


          if (
            previousPoint
          ) {

            canvas.push({

              type:
                "line",

              x1:
                previousPoint.x,

              y1:
                previousPoint.y,

              x2:
                point.x,

              y2:
                point.y,

              lineWidth:
                2,

              lineColor:
                "#ff3b3b"
            });
          }


          previousPoint =
            point;
        }
      );


      // ---------------------------------------------------
      // GRAPH CANVAS
      // ---------------------------------------------------

      content.push({

        canvas,

        margin: [
          0,
          0,
          0,
          0
        ]
      });
// ---------------------------------------------------
// TEMPERATURE SCALE ON LEFT
// ---------------------------------------------------

content.push({

  stack:
    temperatureLabels.map(
      (item, index) => {

        const previousY =
          index === 0
            ? 0
            : temperatureLabels[
                index - 1
              ].y;


        return {

          text:
            `${item.temperature}°`,

          font:
            "Lato",

          fontSize:
            6,

          color:
            "#e53935",

          alignment:
            "right",

          margin: [

            0,

            index === 0
              ? item.y - 4
              : item.y -
                previousY -
                7,

            contentWidth -
              left +
              7,

            0
          ]
        };
      }
    ),

  // Move the labels back over the graph vertically
  margin: [
    0,
    -(top + graphHeight),
    0,
    0
  ]
});

      // ===================================================
      // WIND ROWS
      // BELOW GRAPH
      // ===================================================

      content.push({

        columns: [

          {
            width:
              left,

            text:
              ""
          },


          {
            width:
              graphWidth,

            table: {

              widths:
                weatherData.map(
                  () => "*"
                ),

              body: [

                // NO CLOUDS HERE ANYMORE
                windDirectionRow,
                windSpeedRow
              ]
            },


            layout: {

              hLineWidth:
                () => 0,

              vLineWidth:
                () => 0,

              paddingLeft:
                () => 0,

              paddingRight:
                () => 0,

              paddingTop:
                () => 0,

              paddingBottom:
                () => 0
            }
          },


          {
            width:
              right,

            text:
              ""
          }
        ],


        columnGap:
          0,


        margin: [
          0,
          5,
          0,
          0
        ]
      });


      // ---------------------------------------------------
      // WIND UNIT
      // ---------------------------------------------------

      content.push({

        text:
          "m/s",

        alignment:
          "right",

        font:
          "Lato",

        fontSize:
          6,

        color:
          "#777777",

        margin: [
          0,
          1,
          right,
          8
        ]
      });
    }
  }

  // -------------------------------------------------------
  // SELECTED WIND INFORMATION
  // -------------------------------------------------------

  content.push({

    table: {

      widths: [
        "*",
        "*"
      ],

      body: [

        [
          {
            text:
              "Tuuli",

            bold:
              true,

            font:
              "Lato",

            fontSize
          },


          {
            text:
              "Tuulen suunta",

            bold:
              true,

            font:
              "Lato",

            fontSize
          }
        ],


        [
          {
            text:
              selectedWindSpeed !== null
                ? `${selectedWindSpeed.toFixed(
                    1
                  )} m/s`
                : "-",

            font:
              "Lato",

            fontSize
          },


          {
            text:
              selectedWindDirection ||
              "-",

            font:
              "Lato",

            fontSize
          }
        ]
      ]
    },


    layout: {

      hLineWidth:
        () => 0,

      vLineWidth:
        () => 0,

      paddingLeft:
        () => 4,

      paddingRight:
        () => 4,

      paddingTop:
        () => 3,

      paddingBottom:
        () => 3
    },


    margin: [
      0,
      0,
      0,
      bottomMarginItemGlobal
    ]
  });
}


// =========================================================
// HELPERS
// =========================================================


// ---------------------------------------------------------
// VALID NUMBER
// ---------------------------------------------------------

function toValidNumber(value) {

  // Number(null) === 0
  // Number("") === 0
  //
  // Reject missing values before Number().

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }


  const number =
    Number(value);


  return Number.isFinite(
    number
  )
    ? number
    : null;
}


// ---------------------------------------------------------
// DATE
// ---------------------------------------------------------

function formatDate(date) {

  if (!date) {
    return "-";
  }


  try {

    return new Date(
      `${date}T12:00:00`
    ).toLocaleDateString(
      "fi-FI"
    );

  } catch {

    return date;
  }
}


// ---------------------------------------------------------
// SELECTED TIME -> HOUR
// ---------------------------------------------------------

function getHourFromTime(time) {

  if (!time) {
    return null;
  }


  const hour =
    Number(
      String(time)
        .split(":")[0]
    );


  return Number.isFinite(
    hour
  )
    ? hour
    : null;
}


// ---------------------------------------------------------
// WEATHER ITEM -> HOUR
// ---------------------------------------------------------

function getWeatherHour(item) {

  if (
    item?.hour
  ) {

    const hour =
      Number(
        String(
          item.hour
        ).split(":")[0]
      );


    if (
      Number.isFinite(
        hour
      )
    ) {
      return hour;
    }
  }


  if (
    item?.time
  ) {

    const date =
      new Date(
        item.time
      );


    if (
      !Number.isNaN(
        date.getTime()
      )
    ) {

      /*
       * FMI timestamps are UTC.
       * We need the Finnish local hour.
       */
      const hour =
        Number(
          date.toLocaleTimeString(
            "fi-FI",
            {
              hour:
                "2-digit",

              hour12:
                false,

              timeZone:
                "Europe/Helsinki"
            }
          )
        );


      if (
        Number.isFinite(
          hour
        )
      ) {
        return hour;
      }
    }
  }


  return null;
}


// ---------------------------------------------------------
// WEATHER TYPE
// ---------------------------------------------------------

function getWeatherType(item) {

  const cloudiness =
    toValidNumber(
      item?.cloudiness
    );


  const precipitation =
    toValidNumber(
      item?.precipitation
    );


  const temperature =
    toValidNumber(
      item?.temperature
    );


  // No weather-condition information.
  //
  // IMPORTANT:
  // Do not interpret missing cloudiness as 0,
  // because that would incorrectly mean sunny.

  if (
    cloudiness === null &&
    precipitation === null
  ) {
    return null;
  }


  // Snow

  if (
    precipitation !== null &&
    precipitation >= 0.1 &&
    temperature !== null &&
    temperature <= 1
  ) {
    return "snow";
  }


  // Rain

  if (
    precipitation !== null &&
    precipitation >= 0.1
  ) {
    return "rain";
  }


  // No cloud data -> unknown.

  if (
    cloudiness === null
  ) {
    return null;
  }


  if (
    cloudiness <= 10
  ) {
    return "sun";
  }


  if (
    cloudiness <= 60
  ) {
    return "partlyCloudy";
  }


  return "cloud";
}


// ---------------------------------------------------------
// DRAW WEATHER ICON
// ---------------------------------------------------------

function drawWeatherIcon(
  canvas,
  type,
  x,
  y
) {

  if (!type) {
    return;
  }


  if (
    type === "sun"
  ) {

    drawSun(
      canvas,
      x,
      y
    );

    return;
  }


  if (
    type ===
    "partlyCloudy"
  ) {

    drawSun(
      canvas,
      x - 2,
      y - 1,
      3
    );


    drawCloud(
      canvas,
      x + 1,
      y + 2
    );

    return;
  }


  if (
    type === "cloud"
  ) {

    drawCloud(
      canvas,
      x,
      y
    );

    return;
  }


  if (
    type === "rain"
  ) {

    drawCloud(
      canvas,
      x,
      y - 2
    );


    drawRain(
      canvas,
      x,
      y + 4
    );

    return;
  }


  if (
    type === "snow"
  ) {

    drawCloud(
      canvas,
      x,
      y - 2
    );


    drawSnow(
      canvas,
      x,
      y + 5
    );
  }
}


// ---------------------------------------------------------
// SUN
// ---------------------------------------------------------

function drawSun(
  canvas,
  x,
  y,
  radius = 3
) {

  const rayStart =
    radius + 1.5;


  const rayEnd =
    radius + 3;


  canvas.push({

    type:
      "ellipse",

    x:
      x - radius,

    y:
      y - radius,

    r1:
      radius,

    r2:
      radius,

    color:
      "#f5b400",

    lineColor:
      "#f5b400"
  });


  for (
    let angle = 0;
    angle < 360;
    angle += 45
  ) {

    const radians =
      angle *
      Math.PI /
      180;


    canvas.push({

      type:
        "line",

      x1:
        x +
        Math.cos(
          radians
        ) *
        rayStart,

      y1:
        y +
        Math.sin(
          radians
        ) *
        rayStart,

      x2:
        x +
        Math.cos(
          radians
        ) *
        rayEnd,

      y2:
        y +
        Math.sin(
          radians
        ) *
        rayEnd,

      lineColor:
        "#f5b400",

      lineWidth:
        0.8
    });
  }
}


// ---------------------------------------------------------
// CLOUD
// ---------------------------------------------------------

function drawCloud(
  canvas,
  x,
  y
) {

  const color =
    "#9da5ad";


  // Left bump

  canvas.push({

    type:
      "ellipse",

    x:
      x - 5,

    y:
      y - 1,

    r1:
      3,

    r2:
      2.5,

    color,

    lineColor:
      color
  });


  // Middle bump

  canvas.push({

    type:
      "ellipse",

    x:
      x - 2,

    y:
      y - 4,

    r1:
      4,

    r2:
      4,

    color,

    lineColor:
      color
  });


  // Right bump

  canvas.push({

    type:
      "ellipse",

    x:
      x + 2,

    y:
      y - 2,

    r1:
      3.5,

    r2:
      3,

    color,

    lineColor:
      color
  });


  // Bottom

  canvas.push({

    type:
      "rect",

    x:
      x - 5,

    y:
      y - 1,

    w:
      10,

    h:
      4,

    color,

    lineColor:
      color
  });
}


// ---------------------------------------------------------
// RAIN
// ---------------------------------------------------------

function drawRain(
  canvas,
  x,
  y
) {

  [
    -3,
    0,
    3
  ].forEach(
    (offset) => {

      canvas.push({

        type:
          "line",

        x1:
          x +
          offset,

        y1:
          y,

        x2:
          x +
          offset -
          1,

        y2:
          y +
          3,

        lineColor:
          "#3498db",

        lineWidth:
          0.8
      });
    }
  );
}


// ---------------------------------------------------------
// SNOW
// ---------------------------------------------------------

function drawSnow(
  canvas,
  x,
  y
) {

  [
    -3,
    1,
    4
  ].forEach(
    (offset) => {

      canvas.push({

        type:
          "ellipse",

        x:
          x +
          offset -
          0.6,

        y:
          y -
          0.6,

        r1:
          0.6,

        r2:
          0.6,

        color:
          "#5dade2",

        lineColor:
          "#5dade2"
      });
    }
  );
}


// ---------------------------------------------------------
// WIND DIRECTION TEXT
// ---------------------------------------------------------

function windDirectionToFinnish(
  degrees
) {

  const value =
    toValidNumber(
      degrees
    );


  if (
    value === null
  ) {
    return "";
  }


  const directions = [
    "Pohjoinen",
    "Koillinen",
    "Itä",
    "Kaakko",
    "Etelä",
    "Lounas",
    "Länsi",
    "Luode"
  ];


  const normalized =
    (
      (
        value %
        360
      ) +
      360
    ) %
    360;


  const index =
    Math.round(
      normalized /
      45
    ) %
    8;


  return directions[
    index
  ];
}


// ---------------------------------------------------------
// CIRCULAR WIND DIRECTION AVERAGE
// ---------------------------------------------------------

function circularAverageDegrees(
  values
) {

  if (
    !Array.isArray(
      values
    ) ||
    values.length === 0
  ) {
    return null;
  }


  let sinSum =
    0;

  let cosSum =
    0;

  let count =
    0;


  values.forEach(
    (value) => {

      const degrees =
        toValidNumber(
          value
        );


      if (
        degrees === null
      ) {
        return;
      }


      const radians =
        degrees *
        Math.PI /
        180;


      sinSum +=
        Math.sin(
          radians
        );


      cosSum +=
        Math.cos(
          radians
        );


      count++;
    }
  );


  if (
    !count
  ) {
    return null;
  }


  /*
   * If directions cancel each other almost
   * completely, there is no meaningful
   * average direction.
   */

  const magnitude =
    Math.sqrt(
      sinSum *
      sinSum +
      cosSum *
      cosSum
    );


  if (
    magnitude <
    0.000001
  ) {
    return null;
  }


  const radians =
    Math.atan2(
      sinSum /
      count,

      cosSum /
      count
    );


  return (
    radians *
    180 /
    Math.PI +
    360
  ) %
  360;
}


// ---------------------------------------------------------
// DRAW WIND DIRECTION ARROW
// ---------------------------------------------------------

function drawWindArrow(
  canvas,
  x,
  y,
  degrees
) {

  const direction =
    toValidNumber(
      degrees
    );


  if (
    direction === null
  ) {
    return;
  }


  /*
   * FMI windDirection:
   *
   * 0°   = north
   * 90°  = east
   * 180° = south
   * 270° = west
   *
   * The arrow is drawn directly using pdfMake canvas,
   * so it does not depend on Lato supporting Unicode
   * arrow characters.
   */


  const radians =
    direction *
    Math.PI /
    180;


  const length =
    5;


  const halfLength =
    length /
    2;


  /*
   * PDF Y coordinates increase downwards.
   */

  const dx =
    Math.sin(
      radians
    );


  const dy =
    -Math.cos(
      radians
    );


  const x1 =
    x -
    dx *
    halfLength;


  const y1 =
    y -
    dy *
    halfLength;


  const x2 =
    x +
    dx *
    halfLength;


  const y2 =
    y +
    dy *
    halfLength;


  // Main arrow line

  canvas.push({

    type:
      "line",

    x1,

    y1,

    x2,

    y2,

    lineColor:
      "#57b847",

    lineWidth:
      1.2
  });


  // -------------------------------------------------------
  // ARROW HEAD
  // -------------------------------------------------------

  const headLength =
    2.2;


  const headAngle =
    30 *
    Math.PI /
    180;


  const arrowAngle =
    Math.atan2(
      y2 - y1,
      x2 - x1
    );


  const leftAngle =
    arrowAngle +
    Math.PI -
    headAngle;


  const rightAngle =
    arrowAngle +
    Math.PI +
    headAngle;


  canvas.push({

    type:
      "line",

    x1:
      x2,

    y1:
      y2,

    x2:
      x2 +
      Math.cos(
        leftAngle
      ) *
      headLength,

    y2:
      y2 +
      Math.sin(
        leftAngle
      ) *
      headLength,

    lineColor:
      "#57b847",

    lineWidth:
      1.2
  });


  canvas.push({

    type:
      "line",

    x1:
      x2,

    y1:
      y2,

    x2:
      x2 +
      Math.cos(
        rightAngle
      ) *
      headLength,

    y2:
      y2 +
      Math.sin(
        rightAngle
      ) *
      headLength,

    lineColor:
      "#57b847",

    lineWidth:
      1.2
  });
}